import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnMeta } from '@/types/dataset';
import type { DashboardConfig } from '@/types/dashboard-config';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { planImport, type ColumnResolution } from './import-config';
import { rankCandidates } from './column-match';
import { performImport } from './apply';

type Props = {
  open: boolean;
  onClose: () => void;
  config: DashboardConfig;
  targetDatasetId: string;
  currentColumns: ColumnMeta[];
  onImported: () => void;
};

type ReviewDecision =
  | { kind: 'map'; to: string }
  | { kind: 'skip' };

/**
 * Unified review row — covers both auto-resolved and missing columns
 * so every mapping is visible and editable by the user.
 */
type ReviewRow = {
  sourceName: string;
  sourceMeta: ColumnMeta;
  /** True when the column name exists verbatim in the current dataset. */
  autoMatched: boolean;
  /** Ranked fuzzy candidates (empty when auto-matched). */
  candidates: Array<{ column: ColumnMeta; score: number }>;
};

/**
 * Import review UI — lists EVERY referenced column from the exported
 * config and lets the user verify or change the mapping. Auto-resolved
 * columns (exact name match) are pre-selected but fully editable so
 * the user can override them if, for example, a column was renamed
 * across Excel versions.
 */
export function ImportReviewModal({
  open,
  onClose,
  config,
  targetDatasetId,
  currentColumns,
  onImported,
}: Props): JSX.Element | null {
  const { t } = useTranslation();
  const trapRef = useFocusTrap(open);
  const plan = useMemo(
    () => planImport(config, currentColumns),
    [config, currentColumns],
  );

  // Build a unified row list: auto-resolved + needs-review
  const allRows: ReviewRow[] = useMemo(() => {
    const rows: ReviewRow[] = [];

    // Auto-resolved columns — exact name match.
    for (const ar of plan.autoResolved) {
      if (ar.kind !== 'map') continue;
      const meta = config.sourceColumns.find((c) => c.name === ar.from) ?? {
        name: ar.from,
        sqlType: 'VARCHAR',
      };
      rows.push({
        sourceName: ar.from,
        sourceMeta: meta,
        autoMatched: true,
        candidates: [],
      });
    }

    // Missing columns — fuzzy candidates already computed.
    for (const item of plan.needsReview) {
      rows.push({
        sourceName: item.source.name,
        sourceMeta: item.source,
        autoMatched: false,
        candidates: item.candidates,
      });
    }

    return rows;
  }, [plan, config.sourceColumns]);

  // Decisions — pre-populated for every row.
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>(
    () => {
      const out: Record<string, ReviewDecision> = {};
      for (const row of allRows) {
        if (row.autoMatched) {
          // Pre-select the verbatim match.
          out[row.sourceName] = { kind: 'map', to: row.sourceName };
        } else {
          // Pre-select the best fuzzy candidate if available.
          const first = row.candidates[0];
          if (first) out[row.sourceName] = { kind: 'map', to: first.column.name };
        }
      }
      return out;
    },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  function setDecision(from: string, next: ReviewDecision): void {
    setDecisions((prev) => ({ ...prev, [from]: next }));
  }

  // Pending = rows with no decision at all
  const pending = allRows.filter((row) => !decisions[row.sourceName]);
  const canApply = pending.length === 0 && !busy;

  async function handleApply(): Promise<void> {
    const resolutions: ColumnResolution[] = [];
    for (const row of allRows) {
      const d = decisions[row.sourceName];
      if (!d) continue;
      if (d.kind === 'skip') {
        resolutions.push({ kind: 'skip', from: row.sourceName });
      } else {
        resolutions.push({
          kind: 'map',
          from: row.sourceName,
          to: d.to,
        });
      }
    }
    setBusy(true);
    setError(null);
    try {
      await performImport(targetDatasetId, config, resolutions);
      onImported();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('export.unknownError');
      setError(t('importReview.failed', { message: msg }));
    } finally {
      setBusy(false);
    }
  }

  const autoCount = allRows.filter((r) => r.autoMatched).length;
  const missingCount = allRows.filter((r) => !r.autoMatched).length;

  return createPortal(
    <div
      ref={trapRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 outline-none"
      style={{ background: 'rgba(0,0,0,0.45)' }}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg shadow-xl"
        style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
        }}
      >
        <header
          className="px-5 py-3 border-b flex items-center justify-between shrink-0"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <h2
            className="text-base font-semibold"
            style={{ color: 'var(--color-fg)' }}
          >
            {t('importReview.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-lg px-2 rounded hover:opacity-80"
            style={{ color: 'var(--color-muted)' }}
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {t('importReview.summary.source', { n: config.sourceColumns.length })}{' '}
            {t('importReview.summary.target', { n: currentColumns.length })}{' '}
            <span style={{ color: 'var(--color-accent)' }}>
              {t('importReview.summary.autoMatched', { n: autoCount })}
            </span>{' '}
            <span
              style={{
                color: missingCount > 0 ? 'var(--color-danger, #ef4444)' : undefined,
              }}
            >
              {t('importReview.summary.missing', { n: missingCount })}
            </span>
          </p>

          <div className="flex flex-col gap-2">
            {allRows.map((row) => (
              <ColumnRow
                key={row.sourceName}
                row={row}
                decision={decisions[row.sourceName]}
                currentColumns={currentColumns}
                onDecision={(next) => setDecision(row.sourceName, next)}
              />
            ))}
          </div>

          {error && (
            <div
              className="text-xs p-2 rounded"
              style={{
                background: 'var(--color-danger-bg, rgba(239, 68, 68, 0.1))',
                color: 'var(--color-danger, #ef4444)',
              }}
              role="alert"
            >
              {error}
            </div>
          )}
        </div>

        <footer
          className="px-5 py-3 border-t flex items-center justify-end gap-2 shrink-0"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded hover:opacity-80"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleApply()}
            disabled={!canApply}
            className="text-xs px-3 py-1.5 rounded text-white"
            style={{
              background: canApply
                ? 'var(--color-accent)'
                : 'var(--color-muted)',
              opacity: canApply ? 1 : 0.6,
            }}
          >
            {busy ? t('common.applying') : t('common.apply')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Per-column row
// ---------------------------------------------------------------------------

function ColumnRow({
  row,
  decision,
  currentColumns,
  onDecision,
}: {
  row: ReviewRow;
  decision: ReviewDecision | undefined;
  currentColumns: ColumnMeta[];
  onDecision: (d: ReviewDecision) => void;
}): JSX.Element {
  const { t } = useTranslation();
  // Build options: for auto-matched, put the exact match first then all others.
  // For missing, use fuzzy candidates first then remaining columns.
  const options = useMemo(() => {
    const opts: Array<{ name: string; sqlType: string; label: string }> = [];
    const seen = new Set<string>();

    if (row.autoMatched) {
      // The verbatim match comes first.
      const exact = currentColumns.find((c) => c.name === row.sourceName);
      if (exact) {
        opts.push({
          name: exact.name,
          sqlType: exact.sqlType,
          label: `${exact.name} (${exact.sqlType}) — ${t('importReview.exactMatchSuffix')}`,
        });
        seen.add(exact.name);
      }
      // Then all other candidates ranked by fuzzy score.
      const ranked = rankCandidates(row.sourceMeta, currentColumns, 0);
      for (const c of ranked) {
        if (seen.has(c.column.name)) continue;
        opts.push({
          name: c.column.name,
          sqlType: c.column.sqlType,
          label: `${c.column.name} (${c.column.sqlType}) — ${Math.round(c.score * 100)}%`,
        });
        seen.add(c.column.name);
      }
    } else {
      // Fuzzy candidates first.
      for (const c of row.candidates) {
        opts.push({
          name: c.column.name,
          sqlType: c.column.sqlType,
          label: `${c.column.name} (${c.column.sqlType}) — ${Math.round(c.score * 100)}%`,
        });
        seen.add(c.column.name);
      }
    }

    // Remaining columns not yet listed.
    for (const c of currentColumns) {
      if (seen.has(c.name)) continue;
      opts.push({
        name: c.name,
        sqlType: c.sqlType,
        label: `${c.name} (${c.sqlType})`,
      });
    }

    return opts;
  }, [row, currentColumns, t]);

  const isSkipped = decision?.kind === 'skip';
  const mappedTo = decision?.kind === 'map' ? decision.to : '';

  return (
    <div
      className="p-3 rounded flex flex-col gap-2"
      style={{
        background: 'var(--color-surface)',
        border: `1px solid ${
          row.autoMatched
            ? 'var(--color-border)'
            : 'var(--color-danger, #ef4444)'
        }`,
        opacity: isSkipped ? 0.5 : 1,
      }}
    >
      {/* Source column header */}
      <div className="flex items-center gap-2">
        <span
          className="text-xs"
          style={{
            color: row.autoMatched
              ? 'var(--color-accent)'
              : 'var(--color-danger, #ef4444)',
          }}
        >
          {row.autoMatched ? '✅' : '❌'}
        </span>
        <div className="text-sm font-medium" style={{ color: 'var(--color-fg)' }}>
          {row.sourceName}
          <span
            className="ml-2 text-[11px] font-normal"
            style={{ color: 'var(--color-muted)' }}
          >
            ({row.sourceMeta.sqlType})
          </span>
        </div>
      </div>

      {/* Mapping controls */}
      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: 'var(--color-muted)' }}>→</span>
        <select
          value={mappedTo}
          onChange={(e) =>
            onDecision({ kind: 'map', to: e.target.value })
          }
          disabled={isSkipped}
          className="text-xs p-1 rounded flex-1"
          style={{
            background: 'var(--color-bg)',
            color: 'var(--color-fg)',
            border: '1px solid var(--color-border)',
          }}
        >
          {options.length === 0 && (
            <option value="">{t('importReview.noSuggestion')}</option>
          )}
          {options.map((o) => (
            <option key={o.name} value={o.name}>
              {o.label}
            </option>
          ))}
        </select>

        <label
          className="text-xs flex items-center gap-1 shrink-0 cursor-pointer"
          style={{ color: 'var(--color-muted)' }}
        >
          <input
            type="checkbox"
            checked={isSkipped}
            onChange={(e) => {
              if (e.target.checked) {
                onDecision({ kind: 'skip' });
              } else {
                const first = options[0];
                onDecision({ kind: 'map', to: first?.name ?? '' });
              }
            }}
          />
          {t('importReview.skip')}
        </label>
      </div>
    </div>
  );
}
